import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import * as tenWebApiActions from '../actions/10web-api.actions';
import {map, mergeMap, filter, switchMap, catchError, first} from 'rxjs/operators';
import {ResellerApiService} from '../../components/pages/10web-API/services/10web-reseller-api.service';
import {GetResellerClientData} from '../actions';
import {DbResponse} from '../../shared/interceptors/db-response';
import {from, of} from 'rxjs';
import {Store} from '@ngrx/store';
import {DbStoreState} from '../reducers';

@Injectable()
export class TenWebApiEffects {
  constructor(
    private _actions$: Actions,
    private _resellerApiService: ResellerApiService,
    private _store: Store<DbStoreState>
  ) {
  }

  getResellerClientData$ = createEffect(() => this._actions$.pipe(
    ofType(tenWebApiActions.GET_RESELLER_CLIENT_DATA),
    mergeMap((action: GetResellerClientData) => {
      return this._resellerApiService.getResellerClientData()
        .pipe(
          filter((response: DbResponse) => !!response),
          map((response: DbResponse) => {
            if (response.success) {
              if (response.success && response.data?.products) {
                const products = response.data.products;
                const allPlugins = [
                  ...(products['10web_products'] || []),
                  ...(products.wp_products || [])
                ];

                if (allPlugins.length > 0) {
                    this._store.dispatch(new tenWebApiActions.LoadWordPressPluginDetails({plugins: allPlugins}));
                }
              }
              return new tenWebApiActions.GetResellerClientDataSuccess(response);
            }
            return new tenWebApiActions.GetResellerClientDataFail(response.message);
          })
        );
    })
  ));

  getResellerMainDomain$ = createEffect(() => this._actions$.pipe(
    ofType(tenWebApiActions.GET_RESELLER_MAIN_DOMAIN),
    mergeMap((action: GetResellerClientData) => {
      return this._resellerApiService.getResellerMainDomain()
        .pipe(
          filter((response: DbResponse) => !!response),
          map((response: DbResponse) => {
            if (response.success) {
              return new tenWebApiActions.GetResellerMainDomainSuccess(response);
            }
            return new tenWebApiActions.GetResellerMainDomainFail(response.message);
          })
        );
    })
  ));

  // Effect to handle loading WordPress plugin details
  loadWordPressPluginDetails$ = createEffect(() => this._actions$.pipe(
    ofType(tenWebApiActions.LOAD_WORDPRESS_PLUGIN_DETAILS),
    switchMap((action: tenWebApiActions.LoadWordPressPluginDetails) => {
      const { plugins } = action.payload;
      const pluginsWithSlugs = plugins.filter(p => !!p.slug);

      if (pluginsWithSlugs.length === 0) {
        return of(new tenWebApiActions.LoadWordPressPluginDetailsSuccess({ enhancedPlugins: [] }));
      }

      // Process plugins sequentially to avoid overwhelming the API
      return from(this.processPluginsSequentially(pluginsWithSlugs))
        .pipe(
          map(enhancedPlugins => {
            return new tenWebApiActions.LoadWordPressPluginDetailsSuccess({ enhancedPlugins });
          }),
          catchError(error => of(new tenWebApiActions.LoadWordPressPluginDetailsFail(error)))
        );
    })
  ));

  // Helper method to process plugins sequentially
  private async processPluginsSequentially(plugins: any[]): Promise<any[]> {
    const enhancedPlugins = [...plugins]; // Create a copy to avoid mutating the original

    // Reference data for known 10Web plugins
    const knownPlugins = {
      'tenweb-builder': {
        title: '10Web Builder',
        logo: '/assets/images/logos/builder.jpg',
        author: '10Web'
      },
      'elementor': {
        title: 'Elementor',
        logo: '/assets/images/logos/elementor.jpg',
        author: 'Elementor'
      },
      'tenweb-speed-optimizer': {
        title: '10Web Booster',
        logo: 'https://ps.w.org/tenweb-speed-optimizer/assets/icon-256x256.png?rev=2671739',
        author: '10Web'
      },
      'ai-assistant-by-10web': {
        title: '10Web AI Assistant',
        logo: 'https://ps.w.org/ai-assistant-by-10web/assets/icon-256x256.png?rev=2837207',
        author: '10Web'
      },
      'ai-assistant-by-10web-seo-pack': {
        title: '10Web AI Assistant SEO Pack',
        logo: 'https://ps.w.org/ai-assistant-by-10web-seo-pack/assets/icon-256x256.png?rev=2939325',
        author: '10Web'
      }
    };

    for (const plugin of enhancedPlugins) {
      if (!plugin.slug) continue;

      // First check if it's a known plugin and apply reference data
      if (knownPlugins[plugin.slug]) {
        const refPlugin = knownPlugins[plugin.slug];
        plugin.title = plugin.title || refPlugin.title;
        plugin.author = plugin.author || refPlugin.author;
        plugin.logo = refPlugin.logo; // Always use reference logo for known plugins
        continue; // Skip API call for known plugins
      }

      try {
        // Only fetch details if the plugin doesn't already have title and author
        if (!plugin.title || !plugin.author || !plugin.logo) {
          const wpDetails = await this._resellerApiService.getWordpressPlugins(plugin.slug)
            .pipe(
              filter((res: DbResponse) => !!res?.success),
              map(res => res.data),
              catchError(() => of(null)),
              filter(data => !!data),
              first()
            )
            .toPromise();

          if (wpDetails) {
            // Find the plugin that exactly matches the slug
            const wpPlugin = wpDetails.find(wp => wp.slug === plugin.slug);

            if (wpPlugin) {
              // Update plugin with matched details
              plugin.title = wpPlugin.title || plugin.title || plugin.slug;
              plugin.author = wpPlugin.author || plugin.author || '';
              plugin.logo = wpPlugin.logo || plugin.logo || `https://ps.w.org/${plugin.slug}/assets/icon-256x256.png`;
            } else if (wpDetails[0]) {
              // Fallback to first result if no exact match
              const fallbackPlugin = wpDetails[0];
              plugin.title = plugin.title || fallbackPlugin.title || plugin.slug;
              plugin.author = plugin.author || fallbackPlugin.author || '';
              plugin.logo = plugin.logo || fallbackPlugin.logo || `https://ps.w.org/${plugin.slug}/assets/icon-256x256.png`;
            }
          }
        }
      } catch (error) {
        console.error(`Error fetching details for ${plugin.slug}:`, error);
        // Ensure plugin has at least basic data
        plugin.title = plugin.title || plugin.slug;
        plugin.author = plugin.author || '';
        plugin.logo = plugin.logo || `https://ps.w.org/${plugin.slug}/assets/icon-256x256.png`;
      }
    }

    return enhancedPlugins;
  }

  // Effect to handle refreshing enhanced plugins after save
  refreshEnhancedPlugins$ = createEffect(() => this._actions$.pipe(
    ofType(tenWebApiActions.REFRESH_ENHANCED_PLUGINS),
    switchMap(() => {
      return this._resellerApiService.getResellerClientData()
        .pipe(
          filter((response: DbResponse) => !!response && !!response.success),
          map((response: DbResponse) => {
            if (response.success && response.data?.products) {
              const products = response.data.products;
              const allPlugins = [
                ...(products['10web_products'] || []),
                ...(products.wp_products || [])
              ];

              if (allPlugins.length > 0) {
                return new tenWebApiActions.LoadWordPressPluginDetails({plugins: allPlugins});
              }
            }

            // If no plugins found, return an empty success action
            return new tenWebApiActions.LoadWordPressPluginDetailsSuccess({ enhancedPlugins: [] });
          }),
          catchError(error => of(new tenWebApiActions.LoadWordPressPluginDetailsFail(error)))
        );
    })
  ));
}
